import { Currencies } from '@constants/enums';
import { ISpreadResponse, FeeDescription } from '@efex-services/rate/types';
import { DateTime } from 'luxon';
import type { Country } from '../../../../configs/language';
import {
  IPayableClient,
  type IClientModal,
  type IPayable,
} from '../../../../services/client/types';

export type PaymentsContextType = {
  data: RawData;
  responseData: PayloadData | undefined;
  responseError: Error | undefined;
  responseLoading: boolean;
  generateTransfer: (data: PayloadData) => void;
  generateSwiftTransfer: (data: PayloadData, enterpriseId: string) => void;
  setFormValues: (data: RawData) => void;
  clearFormValues: () => void;
};

export interface RawData {
  client: IClientModal | undefined;
  currency: Currencies;
  rateData?: RateData;
  exchangeData?: ExchangeData;
  amount: string | undefined;
  desAmount: number | string | undefined;
  reverse: boolean;
  clientCurrency: Currencies;
  resultAmount: string;
  invoices?: File[] | undefined;
  comments: string;
  paymentMethod: keyof typeof PaymentMethods;
  dueDate?: DateTime | undefined;
  isPendingTransfer: boolean;
  isSchedule: boolean;
  isForward: boolean;
  payable?: IPayable | undefined;
  forwardValues?: ISpreadResponse | undefined;
  paymentReason: string;
  feeDescription?: FeeDescription;
  finalExchangeRate?: number;
  activeRateUuid?: string | null;
  isPromoter?: boolean;
}

export interface RawDataFromPayable
  extends Omit<
    RawData,
    | 'client'
    | 'exchangeData'
    | 'invoices'
    | 'paymentMethod'
    | 'rateData'
    | 'resultAmount'
    | 'reverse'
    | 'dueDate'
  > {
  client: IPayableClient;
}

export interface ExchangeData {
  currency: string;
  clientCurrency: string;
  exchangeData: RateData;
}

export interface RateData {
  buy: number;
  sell: number;
  reference: string;
  createdAt: string;
  currency: string;
  desCurrency: string;
  countryAccount: Country;
}

export interface PayloadData {
  amount: number;
  paymentReason: string;
  client_origin: string;
  client_uuid: string;
  comment: string;
  contactId?: string | number | null;
  country_origin: string;
  country_user_origin: Country;
  currency: string;
  des_currency: string;
  desCurrency?: string;
  desAmount: number;
  execution_date: string;
  executionDate?: string;
  invoice?: string[];
  is_pending_transfer: boolean;
  payment_schedule: boolean;
  paymentMethodId?: number;
  reference: string | undefined;
  reverse: boolean;
  transactionTypeId?: number;
  uuid: string;
  saved_amount?: number;
  savedAmount?: number;
  walletBalance?: string;
}

export enum PaymentMethods {
  bank = 'checkbox-bank',
  balance = 'checkbox-account',
  schedule = 'checkbox-schedule',
  forward = 'checkbox-forward',
}

export enum PaymentMethodsState {
  'checkbox-bank' = 'bank',
  'checkbox-account' = 'balance',
  'checkbox-schedule' = 'schedule',
  'checkbox-forward' = 'forward',
}

export enum TransactionTypes {
  payable = 1,
  receivable = 2,
  payment_link = 3,
  swift = 4,
  add_funds = 5,
  withdrawal = 6,
  external_deposit = 15,
  promoter = 20,
  promoterSwift = 21,
}

export const DEPOSIT_TRANSACTION_TYPES = [
  TransactionTypes.add_funds,
  TransactionTypes.external_deposit,
];

export enum NewPaymentMethods {
  'balance' = 1,
  'bank' = 2,
  'schedule' = 3,
  'forward' = 4,
}
